<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { useField } from 'vee-validate'
import { Mic, MicOff, Play, Pause, Loader2, Trash2, AlertCircle } from 'lucide-vue-next'
import type { FormField } from '../../types/formTypeStructure'
import FormFieldWrapper from '../../FormFieldWrapper.vue'
import { useFileBucketManager } from '../../hooks/useFileBucketManager'
import { extractFileNameFromUrl } from '../../utils/fileName'
import { getLocalizedText } from '../../utils/formHelpers'

interface Props {
  field: FormField & { fieldType: 'VOICE' }
  name?: string
  currentLang?: string
  defaultValue?: string
  className?: string
}

interface UploadedVoiceInfo {
  url: string
  filename?: string
}

const props = defineProps<Props>()
const fieldName = props.name ?? props.field.fieldName.toString()
const label = computed(() => getLocalizedText(props.field.label, props.currentLang ?? 'fr'))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

const {
  value,
  errorMessage: fieldErrorMessage,
  handleChange,
  setValue,
  setTouched
} = useField<string | null>(fieldName, undefined, {
  initialValue: props.defaultValue ?? null
})

const isRecording = ref(false)
const audioUrl = ref<string | null>(null)
const remoteInfo = ref<UploadedVoiceInfo | null>(null)
const duration = ref(0)
const errorMessage = ref<string | null>(null)
const mediaRecorderRef = ref<MediaRecorder | null>(null)
const audioRef = ref<HTMLAudioElement | null>(null)
const chunksRef = ref<Blob[]>([])
const intervalRef = ref<number | null>(null)
const didInit = ref(false)

const fileBucketManager = computed(() => 
  props.field.fileToBucketManage
    ? useFileBucketManager(props.field.fileToBucketManage)
    : null
)

const hasUploadSupport = computed(() => fileBucketManager.value?.hasUploadSupport ?? false)
const hasDeleteSupport = computed(() => fileBucketManager.value?.hasDeleteSupport ?? false)
const isUploading = computed(() => fileBucketManager.value?.isUploading ?? false)
const isDeleting = computed(() => fileBucketManager.value?.isDeleting ?? false)
const bucketError = computed(() => fileBucketManager.value?.error ?? null)

const effectiveError = computed(() => errorMessage.value ?? bucketError.value ?? fieldErrorMessage.value)

// Initialiser avec defaultValue
watch(() => props.defaultValue, (newValue) => {
  if (typeof newValue === 'string' && newValue.trim().length > 0 && !didInit.value) {
    audioUrl.value = newValue
    remoteInfo.value = {
      url: newValue,
      filename: extractFileNameFromUrl(newValue) ?? undefined,
    }
    setValue(newValue, false)
    didInit.value = true
  }
}, { immediate: true })

onUnmounted(() => {
  stopTimer()
  if (mediaRecorderRef.value && mediaRecorderRef.value.state !== 'inactive') {
    mediaRecorderRef.value.stop()
  }
  if (audioRef.value) {
    audioRef.value.pause()
  }
  if (audioUrl.value && audioUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(audioUrl.value)
  }
})

const stopTimer = () => {
  if (intervalRef.value !== null) {
    clearInterval(intervalRef.value)
    intervalRef.value = null
  }
}

const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const startRecording = async () => {
  if (
    typeof navigator === 'undefined' ||
    !navigator.mediaDevices ||
    !navigator.mediaDevices.getUserMedia
  ) {
    console.error('L\'API mediaDevices.getUserMedia n\'est pas disponible dans ce contexte.')
    alert('L\'enregistrement audio n\'est pas pris en charge par votre navigateur ou ce contexte (HTTPS requis).')
    return
  }

  fileBucketManager.value?.resetError()
  errorMessage.value = null

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const mediaRecorder = new MediaRecorder(stream)
    mediaRecorderRef.value = mediaRecorder
    chunksRef.value = []

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.value.push(e.data)
      }
    }

    mediaRecorder.onstop = async () => {
      const blob = new Blob(chunksRef.value, { type: 'audio/webm' })

      if (!hasUploadSupport.value) {
        errorMessage.value = 'Configuration d\'upload manquante pour ce champ (fileToBucketManage).'
        stream.getTracks().forEach((track) => track.stop())
        stopTimer()
        return
      }

      const localUrl = URL.createObjectURL(blob)
      audioUrl.value = localUrl
      remoteInfo.value = null

      try {
        const result = await fileBucketManager.value!.uploadFile({
          file: blob,
          fallbackFilename: `voice-${Date.now()}.webm`,
        })
        audioUrl.value = result.url
        remoteInfo.value = {
          url: result.url,
          filename: result.filename ?? extractFileNameFromUrl(result.url) ?? undefined,
        }
        setValue(result.url, true)
        setTouched(true)
        URL.revokeObjectURL(localUrl)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Échec du téléversement de l\'enregistrement.'
        errorMessage.value = message
      }

      stream.getTracks().forEach((track) => track.stop())
      stopTimer()
    }

    mediaRecorder.start()
    isRecording.value = true
    duration.value = 0

    intervalRef.value = window.setInterval(() => {
      duration.value += 1
    }, 1000)
  } catch (error) {
    console.error('Erreur d\'accès au microphone:', error)
    alert('Impossible d\'accéder au microphone. Vérifiez les permissions.')
  }
}

const stopRecording = () => {
  if (mediaRecorderRef.value && isRecording.value) {
    mediaRecorderRef.value.stop()
    isRecording.value = false
    stopTimer()
  }
}

const removeRecording = async () => {
  fileBucketManager.value?.resetError()
  errorMessage.value = null

  if (remoteInfo.value && hasDeleteSupport.value) {
    try {
      await fileBucketManager.value!.deleteFile({
        filename: remoteInfo.value.filename ?? extractFileNameFromUrl(remoteInfo.value.url ?? '') ?? undefined,
        fileUrl: remoteInfo.value.url,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Échec de la suppression de l\'enregistrement distant.'
      errorMessage.value = message
      return
    }
  }

  if (audioUrl.value && audioUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(audioUrl.value)
  }

  audioUrl.value = null
  remoteInfo.value = null
  duration.value = 0
  setValue(null, true)
  setTouched(true)
}

const handlePlayPause = () => {
  if (audioRef.value) {
    if (audioRef.value.paused) {
      audioRef.value.play()
    } else {
      audioRef.value.pause()
    }
  }
}

const handleTimeUpdate = (e: Event) => {
  const audio = e.target as HTMLAudioElement
  if (audio.duration) {
    duration.value = audio.currentTime
  }
}
</script>

<template>
  <div class="voice-field-container">
    <!-- Label dupliqué au-dessus -->
    <div class="voice-field-label-container">
      <span class="voice-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <div class="space-y-4">
        <div
          v-if="!audioUrl"
          :class="[
            'relative border-2 border-dashed rounded-lg cursor-pointer transition-all duration-300 ease-in-out',
            isRecording
              ? 'border-red-500 bg-red-50'
              : 'border-gray-300 hover:border-[#04406E] hover:bg-gray-50',
            props.className
          ]"
        >
          <div class="p-8 text-center">
            <div class="flex flex-col items-center space-y-4">
              <button
                type="button"
                @click="isRecording ? stopRecording : startRecording"
                :disabled="isUploading"
                :class="[
                  'p-4 rounded-full transition-all duration-300',
                  isRecording
                    ? 'bg-red-600 text-white animate-pulse'
                    : 'bg-[#04406E] text-white hover:bg-[#033a5a]'
                ]"
              >
                <Mic v-if="isRecording" :size="32" />
                <MicOff v-else :size="32" />
              </button>

              <div>
                <p class="text-base font-medium text-gray-700 mb-1">
                  {{ isRecording ? 'Enregistrement en cours...' : 'Enregistrer un message vocal' }}
                </p>
                <p v-if="isRecording" class="text-lg font-bold text-red-600">
                  {{ formatDuration(duration) }}
                </p>
                <p v-else class="text-sm text-gray-500">
                  Cliquez sur le bouton pour commencer
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="audioUrl"
          class="space-y-3"
        >
          <div class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
            <div class="flex items-center space-x-3">
              <button
                type="button"
                @click="handlePlayPause"
                :disabled="!audioUrl"
                class="p-2 bg-[#04406E] text-white rounded-full hover:bg-[#033a5a] transition-colors disabled:opacity-60"
              >
                <Play v-if="audioRef?.paused !== false" :size="20" />
                <Pause v-else :size="20" />
              </button>
              <audio
                ref="audioRef"
                :src="audioUrl ?? undefined"
                @timeupdate="handleTimeUpdate"
              />
              <span class="text-sm text-gray-700 font-medium">
                {{ formatDuration(duration) }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <Loader2 v-if="isUploading" :size="18" class="animate-spin text-[#04406E]" />
              <button
                type="button"
                @click="removeRecording"
                :disabled="isDeleting"
                class="p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-60"
              >
                <Loader2 v-if="isDeleting" :size="18" class="animate-spin text-red-500" />
                <Trash2 v-else :size="18" class="text-red-500" />
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="effectiveError"
          class="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600"
        >
          {{ effectiveError }}
        </div>
      </div>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.voice-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.voice-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.voice-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>

